/* IrrigationSupply — guided pump sizing calculator
   Engine is pure and testable; UI binds to it at the bottom.
   Physics: Hazen-Williams friction loss (design C=140 for PE), 1.5 m/s velocity
   limit, NPSH check on the suction side, and pump matching against the Q-H curve.  */
(function (root) {
  'use strict';

  /* ---------------- constants ----------------

     Design values chosen deliberately conservative: under-sizing a pump is the
     failure that costs the customer money, so where a range exists we take the
     end that asks for MORE head, not less.                                    */

  // Pipe internal diameters, mm. AVERAGE bore, not minimum-wall bore — real wall
  // exceeds the EN 12201 minimum, so min-wall figures under-predict friction by
  // about 5%. ID enters friction at the -4.871 power, so this matters.
  // Default class is MDPE/PE80 SDR11, which is what is actually sold in Ireland
  // (e.g. Pipelife QualPlast) and has the smallest bore of the three.
  var PIPE_CLASS = {
    sdr11: {
      label: 'MDPE / PE80 SDR11 (the usual Irish blue/black coil)',
      sizes: [
        { od: 25, id: 20.4 }, { od: 32, id: 26.0 }, { od: 40, id: 32.6 },
        { od: 50, id: 40.8 }, { od: 63, id: 51.4 }, { od: 75, id: 61.2 },
        { od: 90, id: 73.6 }, { od: 110, id: 90.0 }
      ]
    },
    sdr136: {
      label: 'PE100 PN12.5 (SDR 13.6)',
      sizes: [
        { od: 25, id: 20.76 }, { od: 32, id: 26.90 }, { od: 40, id: 33.63 },
        { od: 50, id: 42.16 }, { od: 63, id: 53.01 }, { od: 75, id: 63.10 },
        { od: 90, id: 75.80 }, { od: 110, id: 92.83 }
      ]
    },
    sdr17: {
      label: 'PE100 PN10 (SDR 17)',
      sizes: [
        { od: 25, id: 21.88 }, { od: 32, id: 27.76 }, { od: 40, id: 34.91 },
        { od: 50, id: 43.64 }, { od: 63, id: 54.94 }, { od: 75, id: 65.40 },
        { od: 90, id: 78.40 }, { od: 110, id: 95.80 }
      ]
    }
  };
  var DEFAULT_CLASS = 'sdr11';

  var MAX_VELOCITY = 1.5;        // m/s — Rain Bird / standard irrigation limit
  var HW_C = 140;                // Hazen-Williams DESIGN value for PE (not 150,
                                 // which is a new-pipe figure; 140 allows for ageing
                                 // and matches Darcy-Weisbach far better)
  var FITTINGS = 0.10;           // +10% on pipe friction for bends and valves
  var BAR_TO_M = 10.2;
  var MARGIN = 1.15;             // pump must clear the duty point by 15%
  var SUCTION_DESIGN_LIMIT = 6;  // m — design limit, not the 8 m theoretical ceiling
  var ATMOS_M = 10.3;            // m of water at sea level
  var NPSHR_ALLOW = 3.0;         // m — typical requirement of a self-priming pump
  var NPSH_SAFETY = 0.5;         // m
  var FILTER_LOSS_M = 5.0;       // m across a disc/screen filter at design differential
  var VALVE_LOSS_M = 2.0;        // m across a zone solenoid / regulator

  // Pressure required at the nozzle, bar.
  var EMITTER = {
    drip:  { bar: 1.5,  label: 'drip line' },
    micro: { bar: 2.0,  label: 'micro-sprinklers' },
    rotor: { bar: 3.0,  label: 'rotor sprinklers' },
    gun:   { bar: 4.0,  label: 'rain gun' },
    reel:  { bar: 8.0,  label: 'hose reel travelling irrigator' },
    tap:   { bar: 2.0,  label: 'taps and drinkers' }
  };
  var DEFAULT_EMITTER = 'rotor';

  // Per-unit flow defaults. drip/micro are litres per HOUR; the rest L/min.
  var UNIT = {
    drip:  { per: 2,   unit: 'lph', label: 'Drip emitters',              filtered: true },
    micro: { per: 80,  unit: 'lph', label: 'Micro-sprinklers',           filtered: true },
    rotor: { per: 10,  unit: 'lpm', label: 'Rotor / impact sprinklers',  filtered: false },
    gun:   { per: 100, unit: 'lpm', label: 'Rain gun',                   filtered: false },
    reel:  { per: 250, unit: 'lpm', label: 'Hose reel irrigator',        filtered: false },
    tap:   { per: 12,  unit: 'lpm', label: 'Taps or livestock drinkers', filtered: false }
  };

  var MAX_SUCTION_LIFT = SUCTION_DESIGN_LIMIT;   // kept for the public API

  /* ---------------- hydraulics ---------------- */

  function lpmToM3s(lpm) { return lpm / 60000; }
  function areaM2(idMm) { return Math.PI * Math.pow(idMm / 2000, 2); }
  function finite(n) { return (typeof n === 'number' && isFinite(n)) ? n : 0; }

  function pipeTable(cls) {
    return (PIPE_CLASS[cls] || PIPE_CLASS[DEFAULT_CLASS]).sizes;
  }

  function velocity(lpm, idMm) {
    if (!(idMm > 0) || !(lpm > 0)) return 0;
    return lpmToM3s(lpm) / areaM2(idMm);
  }

  // Hazen-Williams (SI): hf = 10.67 * L * Q^1.852 / (C^1.852 * D^4.871)
  function frictionLoss(lpm, idMm, lengthM, c) {
    if (!(lpm > 0) || !(lengthM > 0) || !(idMm > 0)) return 0;
    c = c || HW_C;
    var Q = lpmToM3s(lpm), D = idMm / 1000;
    return 10.67 * lengthM * Math.pow(Q, 1.852) /
           (Math.pow(c, 1.852) * Math.pow(D, 4.871));
  }

  function maxFlowFor(idMm) { return MAX_VELOCITY * areaM2(idMm) * 60000; }

  function smallestPipeFor(lpm, cls) {
    var t = pipeTable(cls);
    for (var i = 0; i < t.length; i++) if (maxFlowFor(t[i].id) >= lpm) return t[i];
    return null;
  }

  function pipeByOd(od, cls) {
    var t = pipeTable(cls);
    for (var i = 0; i < t.length; i++) if (t[i].od === Number(od)) return t[i];
    return null;
  }

  /* ---------------- flow demand ---------------- */

  function flowBreakdown(groups) {
    var rows = [], total = 0, incomplete = 0, needsFilter = false;
    (groups || []).forEach(function (g, i) {
      var spec = UNIT[g.kind];
      if (!spec) return;
      var per = (g.per > 0 && isFinite(g.per)) ? g.per : spec.per;
      var count = finite(g.count);
      var hasCount = count > 0;
      var lpm = hasCount ? ((spec.unit === 'lph') ? (count * per) / 60 : count * per) : 0;
      if (!hasCount) incomplete++;
      if (hasCount && spec.filtered) needsFilter = true;
      total += lpm;
      rows.push({ index: i, kind: g.kind, label: spec.label, count: count, per: per,
                  perUnit: spec.unit, lpm: lpm, hasCount: hasCount });
    });
    return { total: total, rows: rows, incomplete: incomplete, needsFilter: needsFilter };
  }

  function flowFromGroups(groups) { return flowBreakdown(groups).total; }

  // The emitter needing the most pressure sets the requirement for the zone.
  function governingEmitter(groups) {
    var worst = null;
    (groups || []).forEach(function (g) {
      if (!(finite(g.count) > 0) || !EMITTER[g.kind]) return;
      if (!worst || EMITTER[g.kind].bar > EMITTER[worst].bar) worst = g.kind;
    });
    return worst;
  }

  function emitterSpread(groups) {
    var present = [];
    (groups || []).forEach(function (g) {
      if (finite(g.count) > 0 && EMITTER[g.kind] && present.indexOf(g.kind) < 0) present.push(g.kind);
    });
    if (present.length < 2) return null;
    var bars = present.map(function (k) { return EMITTER[k].bar; });
    var hi = Math.max.apply(null, bars), lo = Math.min.apply(null, bars);
    return (hi - lo >= 1.0) ? { hi: hi, lo: lo, kinds: present } : null;
  }

  /* ---------------- pump type ---------------- */

  var SUCTION_SOURCES = { stream: 1, pond: 1, tank_under: 1, borehole: 1 };

  function pumpType(input) {
    var depth = finite(input.sourceDepth);

    if (input.power === 'none') {
      return { key: 'solar', name: 'Solar pump kit',
        why: 'There is no mains power on site, so a solar kit avoids trenching a cable.' };
    }
    if (input.source === 'borehole' || depth > SUCTION_DESIGN_LIMIT) {
      return { key: 'submersible', name: 'Submersible / borehole pump',
        why: 'Your water sits about ' + depth + ' m below the pump. Past roughly ' + SUCTION_DESIGN_LIMIT +
             ' m of suction lift no surface pump can draw it reliably — the pump has to sit in the water and push.' };
    }
    if (input.source === 'rainwater') {
      return { key: 'rainwater', name: 'Rainwater harvesting system',
        why: 'A tank-fed set with automatic mains changeover keeps supply going when the tank runs low.' };
    }
    if (input.source === 'mains' || input.source === 'tank_gravity') {
      if (input.demand === 'intermittent') {
        return { key: 'vessel', name: 'Pressure vessel set',
          why: 'You already have water but not enough pressure, and demand switches on and off — the vessel stops the pump short-cycling itself to death.' };
      }
      return { key: 'booster', name: 'Booster pump',
        why: 'You already have water at the pump, just not enough pressure. A booster raises pressure; it cannot create flow that is not there.' };
    }
    if (input.demand === 'intermittent') {
      return { key: 'vessel', name: 'Pressure vessel set',
        why: 'Demand switches on and off constantly, so the pump needs a buffer or it will short-cycle.' };
    }
    return { key: 'surface', name: 'Surface / centrifugal pump',
      why: 'Open water within ' + SUCTION_DESIGN_LIMIT + ' m of suction lift, and you need volume — a self-priming surface pump with a foot valve suits.' };
  }

  /* ---------------- the calculation ---------------- */

  function calculate(input) {
    input = input || {};
    var warnings = [], notes = [];
    var stop = function (msg) { return { ok: false, error: msg }; };

    /* --- flow --- */
    var usingKnown = finite(input.flowKnown) > 0;
    var breakdown = usingKnown ? null : flowBreakdown(input.groups);
    var flowRaw = usingKnown ? finite(input.flowKnown) : breakdown.total;

    if (!(flowRaw > 0)) {
      var filled = breakdown ? breakdown.rows.filter(function (r) { return r.hasCount; }).length : 0;
      if (usingKnown || !breakdown || !breakdown.rows.length || filled === 0) {
        return stop('Enter how much water you need. Fill in the "How many" box on at least one emitter row, or switch to "I already know my flow rate".');
      }
      return stop('Those figures come to zero. Check the "how many" and per-emitter numbers on each row.');
    }
    if (flowRaw > 2000) return stop('That flow is beyond what this tool covers. Send us the figures and we will size the main by hand.');

    /* --- pipe run is required: a blank box used to give zero friction silently --- */
    var runM = finite(input.pipeRun);
    if (!(runM > 0)) {
      return stop('Enter the pipe run in metres. Without it we cannot work out friction loss, and a duty point without friction is not a duty point.');
    }
    if (runM > 5000) return stop('That pipe run is beyond what this tool covers. Send us the details and we will do it by hand.');

    /* --- emitter pressure --- */
    var emitterKey = input.emitterOverride || governingEmitter(input.groups) || DEFAULT_EMITTER;
    if (!EMITTER[emitterKey]) emitterKey = DEFAULT_EMITTER;
    var emitterBar = finite(input.emitterBar);
    if (!(emitterBar > 0) || emitterBar > 20) emitterBar = EMITTER[emitterKey].bar;
    var emitterM = emitterBar * BAR_TO_M;

    /* --- geometry. sourceDepth is the lift OUT of the water; staticLift is the
           rise from the pump up to the highest emitter. Both count. --- */
    var depth = finite(input.sourceDepth);
    var isSuction = !!SUCTION_SOURCES[input.source];
    if (!isSuction) depth = 0;
    var rise = finite(input.staticLift);
    var liftTotal = depth + rise;

    if (rise < 0) {
      notes.push('You entered a fall rather than a rise, so gravity is doing part of the work. The pump still has to deliver the emitter pressure, which is the floor on total head.');
    }

    /* --- pipe --- */
    var cls = PIPE_CLASS[input.pipeClass] ? input.pipeClass : DEFAULT_CLASS;
    var recommended = smallestPipeFor(flowRaw, cls);
    var chosen = input.pipeOd ? pipeByOd(input.pipeOd, cls) : null;
    var usedPipe = chosen || recommended;
    if (!usedPipe) {
      var t = pipeTable(cls);
      usedPipe = t[t.length - 1];
      warnings.push({ level: 'stop', text:
        'At ' + Math.round(flowRaw) + ' L/min you are past the pipe sizes this tool covers. Treat the figures below as a floor, not an answer, and send them to us.' });
    }

    var vel = velocity(flowRaw, usedPipe.id);
    var friction = frictionLoss(flowRaw, usedPipe.id, runM, HW_C) * (1 + FITTINGS);

    if (chosen && vel > MAX_VELOCITY) {
      warnings.push({ level: 'warn', text:
        'Your ' + chosen.od + ' mm pipe would run at ' + vel.toFixed(1) + ' m/s. Keep it under ' + MAX_VELOCITY +
        ' m/s — go up to ' + (recommended ? recommended.od + ' mm' : 'a larger main') +
        '. Undersized pipe is the most expensive mistake on an irrigation job, and you pay for it every month in electricity.' });
    }

    /* --- component losses: a filter is not optional on drip or micro --- */
    var wantsFilter = breakdown ? breakdown.needsFilter : (emitterKey === 'drip' || emitterKey === 'micro');
    var componentM = (input.componentLoss != null && finite(input.componentLoss) >= 0)
      ? finite(input.componentLoss)
      : (wantsFilter ? FILTER_LOSS_M + VALVE_LOSS_M : VALVE_LOSS_M);

    /* --- incoming pressure, for a mains-fed booster --- */
    var mainsBar = (input.source === 'mains') ? finite(input.mainsPressure) : 0;
    if (mainsBar > 10) mainsBar = 0;
    var mainsM = mainsBar * BAR_TO_M;

    var grossHead = liftTotal + friction + emitterM + componentM;
    var totalHead = grossHead - mainsM;

    // The pump can never be asked for less than the emitter needs.
    if (totalHead < emitterM && mainsM > 0) {
      notes.push('Your incoming mains pressure already covers most of this duty. Check whether you need a pump at all before buying one — a booster on an already-adequate supply wastes money and can over-pressure drip line.');
    }
    if (totalHead < 0) totalHead = 0;

    var type = pumpType(input);

    /* --- suction-side check: NPSH, not just a depth rule --- */
    if (isSuction && type.key !== 'submersible') {
      var suctionRun = finite(input.suctionRun);
      var suctionPipe = chosen || recommended || usedPipe;
      var suctionFriction = suctionRun > 0
        ? frictionLoss(flowRaw, suctionPipe.id, suctionRun, HW_C) * (1 + FITTINGS)
        : 0;
      var npshA = ATMOS_M - depth - suctionFriction - 0.12;   // 0.12 m vapour at 10 C
      if (depth > SUCTION_DESIGN_LIMIT) {
        warnings.push({ level: 'stop', text:
          'Water ' + depth + ' m below the pump is past the ' + SUCTION_DESIGN_LIMIT +
          ' m design limit for suction lift. Use a submersible sitting in the water instead.' });
      } else if (npshA - NPSHR_ALLOW < NPSH_SAFETY) {
        warnings.push({ level: 'stop', text:
          'The suction side will not work: after ' + depth + ' m of lift and ' + suctionFriction.toFixed(1) +
          ' m of friction in the suction pipe there is only ' + npshA.toFixed(1) +
          ' m of head available, and the pump needs about ' + NPSHR_ALLOW +
          ' m. It will cavitate. Shorten or widen the suction line, move the pump closer to the water, or use a submersible.' });
      } else if (suctionRun > 0 && suctionFriction > 1.5) {
        warnings.push({ level: 'warn', text:
          'Your suction line loses ' + suctionFriction.toFixed(1) + ' m to friction. Keep it short and go one pipe size up on the suction side — never smaller than the pump inlet.' });
      }
      if (suctionRun <= 0) {
        notes.push('Suction-line length was left blank, so suction friction is not included. On a surface pump that is the most common cause of a pump that will not prime — enter it if you can.');
      }
    }

    /* --- other advisories --- */
    if (!usingKnown && flowRaw < 1) {
      warnings.push({ level: 'warn', text:
        'That comes to only ' + (Math.round(flowRaw * 100) / 100) + ' L/min. Drip and micro emitters are rated in litres per HOUR, so it takes hundreds of them to add up — 500 drippers at 2 L/h is about 17 L/min. Check the counts before relying on this.' });
    }
    var spread = emitterSpread(input.groups);
    if (spread) {
      warnings.push({ level: 'warn', text:
        'You have mixed emitters needing very different pressures (' + spread.lo + ' bar up to ' + spread.hi +
        ' bar). We have sized for the thirstiest, which means the low-pressure ones will be over-pressured — blown barbs and roughly double the intended application rate. Run them as separate zones, or put a pressure regulator on the low-pressure group.' });
    }
    if (input.source === 'mains' && !(mainsBar > 0)) {
      notes.push('You did not give an incoming mains pressure, so this assumes the pump does all the work. Irish mains often arrives at 2 to 4 bar — measure it with a gauge on an outside tap and re-run this, or you will buy a bigger pump than you need.');
    }
    if (input.source === 'stream') {
      notes.push('Abstraction from a watercourse in Ireland may need registration or authorisation depending on volume. Check with your local authority or the EPA before you commit to a design.');
    }
    if (type.key === 'solar') {
      notes.push('Solar output follows the sun. Size for a dull Irish day, or pair the kit with a buffer tank so the pump fills slowly through daylight and the tank delivers on demand.');
    }
    if (type.key === 'submersible' || isSuction) {
      notes.push('Fit dry-run protection. Running dry destroys the mechanical seal within minutes and is not covered by any warranty.');
    }
    if (input.demand === 'intermittent' && type.key !== 'vessel') {
      notes.push('Because demand switches on and off, add a pressure vessel. Without one the pump restarts on every small draw-off, and that short-cycling is what kills motors.');
    }
    if (wantsFilter) {
      notes.push('The ' + componentM.toFixed(1) + ' m allowed for filtration and valves is a design figure. A blocking filter raises it — fit a pressure gauge either side and flush when the difference grows.');
    }

    var round = function (n, dp) { var f = Math.pow(10, dp == null ? 1 : dp); return Math.round(n * f) / f; };
    var disp = function (n) { return (Math.abs(n) < 10) ? round(n, 2) : round(n, 1); };

    return {
      ok: true,
      flow: disp(flowRaw), flowRaw: flowRaw, breakdown: breakdown,
      depth: round(depth), rise: round(rise), lift: round(liftTotal),
      friction: round(friction), componentM: round(componentM),
      mainsM: round(mainsM), mainsBar: mainsBar,
      emitterM: round(emitterM), emitterKey: emitterKey,
      emitterLabel: EMITTER[emitterKey].label, emitterBar: emitterBar,
      totalHead: round(totalHead), totalBar: round(totalHead / BAR_TO_M, 2),
      velocity: round(vel, 2),
      pipeUsed: usedPipe, pipeRecommended: recommended, pipeWasChosen: !!chosen,
      pipeClass: cls, pipeClassLabel: PIPE_CLASS[cls].label, pipeRun: runM,
      isSuction: isSuction, type: type, warnings: warnings, notes: notes,
      requiredFlow: disp(flowRaw * MARGIN),
      requiredHead: disp(totalHead * MARGIN),
      blocked: warnings.some(function (w) { return w.level === 'stop'; })
    };
  }

  /* ---------------- product matching ----------------

     A pump's maximum flow happens at zero head and its maximum head at zero flow.
     They can NEVER be delivered together, so testing them independently will
     recommend pumps that cannot reach the duty point. If a real Q-H curve is
     supplied we interpolate it; otherwise we fall back to the shutoff parabola
     H(Q) = Hmax * (1 - (Q/Qmax)^2), which is conservative.
     Matching stays dormant until products carry real, supplier-confirmed data.  */

  function headAtFlow(p, q) {
    if (p.curve && p.curve.length > 1) {
      var c = p.curve.slice().sort(function (a, b) { return a[0] - b[0]; });
      if (q <= c[0][0]) return c[0][1];
      if (q >= c[c.length - 1][0]) return 0;
      for (var i = 1; i < c.length; i++) {
        if (q <= c[i][0]) {
          var t = (q - c[i - 1][0]) / (c[i][0] - c[i - 1][0]);
          return c[i - 1][1] + t * (c[i][1] - c[i - 1][1]);
        }
      }
    }
    if (!(p.maxFlow > 0) || !(p.maxHead > 0)) return 0;
    if (q >= p.maxFlow) return 0;
    return p.maxHead * (1 - Math.pow(q / p.maxFlow, 2));
  }

  function matchProducts(result, catalogue) {
    var usable = (catalogue || []).filter(function (p) {
      return (p.maxFlow > 0 && p.maxHead > 0) || (p.curve && p.curve.length > 1);
    });
    if (!usable.length || !(result.totalHead > 0) || result.blocked) {
      return { active: false, fits: [], near: [] };
    }

    var need = result.totalHead * MARGIN;
    var scored = usable.map(function (p) {
      var avail = headAtFlow(p, result.flowRaw);
      var typeOk = !result.type.key || !p.type || p.type === result.type.key;
      return {
        p: p, avail: Math.round(avail * 10) / 10, typeOk: typeOk,
        fits: typeOk && avail >= need,
        headroom: avail - need,
        curveBased: !!(p.curve && p.curve.length > 1)
      };
    });

    var fits = scored.filter(function (s) { return s.fits; })
      .sort(function (a, b) { return a.headroom - b.headroom; });

    var near = scored.filter(function (s) { return !s.fits; })
      .sort(function (a, b) { return b.avail - a.avail; })
      .slice(0, 3)
      .map(function (s) {
        var why = [];
        if (s.avail <= 0) {
          why.push('cannot deliver ' + result.flow + ' L/min at all');
        } else if (s.avail < need) {
          why.push('at ' + result.flow + ' L/min it reaches only ' + s.avail + ' m, and you need ' + Math.round(need * 10) / 10 + ' m');
        }
        if (!s.typeOk) why.push('wrong type for your water source');
        s.why = why.join('; ');
        return s;
      });

    return { active: true, fits: fits, near: near, need: Math.round(need * 10) / 10 };
  }

  var api = {
    PIPE_CLASS: PIPE_CLASS, DEFAULT_CLASS: DEFAULT_CLASS, EMITTER: EMITTER, UNIT: UNIT,
    MAX_VELOCITY: MAX_VELOCITY, MAX_SUCTION_LIFT: MAX_SUCTION_LIFT, BAR_TO_M: BAR_TO_M,
    HW_C: HW_C, MARGIN: MARGIN,
    pipeTable: pipeTable, velocity: velocity, frictionLoss: frictionLoss,
    maxFlowFor: maxFlowFor, smallestPipeFor: smallestPipeFor,
    flowFromGroups: flowFromGroups, flowBreakdown: flowBreakdown,
    headAtFlow: headAtFlow, pumpType: pumpType, calculate: calculate,
    matchProducts: matchProducts
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.ISSizer = api;

})(typeof window !== 'undefined' ? window : globalThis);

/* ============================ UI ============================ */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  var S = window.ISSizer;
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); };

  var SOURCE_LABEL = {
    mains: 'Mains supply', tank_gravity: 'Storage tank (above ground)',
    tank_under: 'Storage tank (underground)', borehole: 'Borehole or well',
    stream: 'Stream or river', pond: 'Pond or lake',
    rainwater: 'Rainwater harvesting tank'
  };
  var POWER_LABEL = { '230': '230 V single phase', '400': '400 V three phase', none: 'No mains power (solar needed)' };
  var DEEP_SOURCES = { tank_under: 1, borehole: 1, stream: 1, pond: 1 };  // sources the pump must lift FROM

  function init(root) {
    var form = $('[data-sizer-form]', root);
    var out = $('[data-sizer-results]', root);
    if (!form || !out) return;

    var catalogue = [];
    var dataEl = $('[data-sizer-products]', root);
    if (dataEl) { try { catalogue = JSON.parse(dataEl.textContent) || []; } catch (e) { catalogue = []; } }

    /* --- conditional fields --- */
    var sourceSel = $('[name=source]', form);
    var depthWrap = $('[data-depth-wrap]', form);
    var suctionWrap = $('[data-suction-wrap]', form);
    var mainsWrap = $('[data-mains-wrap]', form);
    function syncDepth() {
      var suction = !!DEEP_SOURCES[sourceSel.value];
      var mains = sourceSel.value === 'mains';
      // a borehole pump sits IN the water, so it has no suction line to size
      var hasSuctionLine = suction && sourceSel.value !== 'borehole';
      depthWrap.hidden = !suction;
      if (suctionWrap) suctionWrap.hidden = !hasSuctionLine;
      if (mainsWrap) mainsWrap.hidden = !mains;
      if (!suction) $('[name=sourceDepth]', form).value = '';
      if (!hasSuctionLine && suctionWrap) $('[name=suctionRun]', form).value = '';
      if (!mains && mainsWrap) $('[name=mainsPressure]', form).value = '';
    }
    sourceSel.addEventListener('change', syncDepth); syncDepth();

    /* --- known-flow shortcut --- */
    var modeInputs = $$('[name=flowmode]', form);
    var buildWrap = $('[data-build-wrap]', form);
    var knownWrap = $('[data-known-wrap]', form);
    function syncMode() {
      var known = form.querySelector('[name=flowmode]:checked').value === 'known';
      buildWrap.hidden = known; knownWrap.hidden = !known;
      if (typeof updateTotal === 'function') updateTotal();
    }
    modeInputs.forEach(function (i) { i.addEventListener('change', syncMode); }); syncMode();

    /* --- emitter rows --- */
    var rowsWrap = $('[data-rows]', form);
    var tpl = $('[data-row-template]', root);

    function addRow(kind) {
      var node = tpl.content.firstElementChild.cloneNode(true);
      var sel = $('[data-kind]', node), per = $('[data-per]', node);
      if (kind) sel.value = kind;
      function syncPer() {
        var u = S.UNIT[sel.value];
        per.value = u.per;
        $('[data-per-unit]', node).textContent = u.unit === 'lph' ? 'litres per HOUR' : 'L/min';
      }
      sel.addEventListener('change', function () { syncPer(); updateTotal(); });
      syncPer();
      $('[data-remove]', node).addEventListener('click', function () {
        node.remove();
        if (!rowsWrap.children.length) addRow('rotor');
        updateTotal();
      });
      rowsWrap.appendChild(node);
      updateTotal();
    }
    $('[data-add-row]', form).addEventListener('click', function () { addRow('rotor'); });
    addRow('rotor');

    /* --- read the form --- */
    // Every row is returned, including incomplete ones. The engine decides what to
    // do with them and the running total shows the user. Nothing is dropped quietly.
    function readGroups() {
      return $$('[data-row]', rowsWrap).map(function (row) {
        return {
          kind: $('[data-kind]', row).value,
          count: parseFloat($('[data-count]', row).value) || 0,
          per: parseFloat($('[data-per]', row).value) || 0
        };
      });
    }

    /* --- live running total, updated as the user types --- */
    var totalEl = $('[data-flow-total]', root);

    function fmt(n) {
      if (!(n > 0)) return '0';
      return (n < 10) ? String(Math.round(n * 100) / 100) : String(Math.round(n * 10) / 10);
    }

    function updateTotal() {
      if (!totalEl) return;
      if (form.querySelector('[name=flowmode]:checked').value === 'known') {
        totalEl.hidden = true; return;
      }
      var b = S.flowBreakdown(readGroups());
      var parts = b.rows.map(function (r) {
        var each = r.perUnit === 'lph' ? (r.per + ' L/h') : (r.per + ' L/min');
        if (!r.hasCount) {
          return '<span class="sizer__part sizer__part--empty">' + esc(r.label) +
                 ' &mdash; no count entered, so it adds nothing</span>';
        }
        return '<span class="sizer__part">' + r.count + ' &times; ' + esc(r.label).toLowerCase() +
               ' at ' + each + ' = <strong>' + fmt(r.lpm) + ' L/min</strong></span>';
      }).join('');

      totalEl.hidden = false;
      totalEl.innerHTML = parts +
        '<span class="sizer__running">Running total: <strong>' + fmt(b.total) + ' L/min</strong>' +
        (b.incomplete ? ' &middot; ' + b.incomplete + ' row' + (b.incomplete > 1 ? 's' : '') +
          ' still needs a count' : '') + '</span>';
    }

    rowsWrap.addEventListener('input', updateTotal);
    rowsWrap.addEventListener('change', updateTotal);

    function readInput() {
      var known = form.querySelector('[name=flowmode]:checked').value === 'known';
      var num = function (n) { var v = parseFloat($('[name=' + n + ']', form).value); return isNaN(v) ? 0 : v; };
      var rawOrNull = function (n) {
        var el = $('[name=' + n + ']', form);
        if (!el || el.value === '') return null;
        var v = parseFloat(el.value);
        return isNaN(v) ? null : v;
      };
      return {
        source: sourceSel.value,
        sourceDepth: num('sourceDepth'),
        suctionRun: num('suctionRun'),
        mainsPressure: num('mainsPressure'),
        application: $('[name=application]', form).value,
        demand: form.querySelector('[name=demand]:checked').value,
        groups: known ? [] : readGroups(),
        flowKnown: known ? num('flowKnown') : 0,
        emitterOverride: known ? $('[name=emitterKnown]', form).value : '',
        emitterBar: num('emitterBar'),
        staticLift: rawOrNull('staticLift') == null ? 0 : rawOrNull('staticLift'),
        pipeRun: num('pipeRun'),
        pipeOd: $('[name=pipeOd]', form).value || '',
        pipeClass: $('[name=pipeClass]', form) ? $('[name=pipeClass]', form).value : '',
        componentLoss: rawOrNull('componentLoss'),
        power: $('[name=power]', form).value
      };
    }

    /* --- render --- */
    function quoteHref(input, r) {
      var body = 'Sized with the calculator on the Advice page.\n\n' +
        'Duty point: ' + r.flow + ' L/min at ' + r.totalHead + ' m total head (' + r.totalBar + ' bar)\n' +
        '  static lift ' + r.lift + ' m + friction ' + r.friction + ' m + ' + r.emitterLabel +
        ' at ' + r.emitterBar + ' bar (' + r.emitterM + ' m)\n' +
        (r.depth > 0 ? '  water sits ' + r.depth + ' m below the pump\n' : '') +
        (r.mainsM > 0 ? '  less ' + r.mainsBar + ' bar of incoming mains (' + r.mainsM + ' m)\n' : '') +
        '  includes ' + r.componentM + ' m for filtration and valves\n' +
        'Pipe: ' + r.pipeRun + ' m run, ' +
        (r.pipeRecommended ? 'smallest workable size ' + r.pipeRecommended.od + ' mm' : 'oversized for this tool') +
        (r.pipeWasChosen ? ' (I have ' + r.pipeUsed.od + ' mm at ' + r.velocity + ' m/s)' : '') + '\n' +
        'Suggested type: ' + r.type.name + '\n' +
        'Allowing 15% margin: needs at least ' + r.requiredFlow + ' L/min at ' + r.requiredHead + ' m\n\n' +
        'Please confirm this and recommend a pump.';
      var q = [
        'flow=' + encodeURIComponent(r.flow),
        'lift=' + encodeURIComponent(r.lift),
        'run=' + encodeURIComponent(r.pipeRun),
        'source=' + encodeURIComponent(SOURCE_LABEL[input.source] || ''),
        'app=' + encodeURIComponent(input.application || ''),
        'power=' + encodeURIComponent(POWER_LABEL[input.power] || ''),
        'body=' + encodeURIComponent(body)
      ].join('&');
      return '/pages/contact?' + q + '#quote';
    }

    function alertBlock(list, kind) {
      if (!list.length) return '';
      return list.map(function (item) {
        var text = item.text || item;
        var stop = item.level === 'stop';
        return '<p class="sizer__alert' + (stop ? ' sizer__alert--stop' : kind === 'note' ? ' sizer__alert--note' : '') + '">' +
          esc(text) + '</p>';
      }).join('');
    }

    function render(input, r) {
      if (!r.ok) {
        out.hidden = false;
        out.innerHTML = '<p class="sizer__alert sizer__alert--stop">' + esc(r.error) + '</p>';
        out.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      var m = S.matchProducts(r, catalogue);

      var html = '' +
      '<div class="sizer__duty">' +
        '<div><span class="sizer__figure">' + r.flow + '</span><span class="sizer__unit">L/min</span>' +
          '<span class="sizer__cap">Flow demand</span></div>' +
        '<div><span class="sizer__figure">' + r.totalHead + '</span><span class="sizer__unit">m</span>' +
          '<span class="sizer__cap">Total head &middot; ' + r.totalBar + ' bar</span></div>' +
        '<div><span class="sizer__figure">' + (r.pipeRecommended ? r.pipeRecommended.od : '&mdash;') + '</span>' +
          '<span class="sizer__unit">mm</span><span class="sizer__cap">Smallest workable pipe</span></div>' +
      '</div>' +

      (r.breakdown && r.breakdown.rows.length > 1
        ? '<h4 class="sizer__h">Where that flow comes from</h4><table class="sizer__table"><tbody>' +
          r.breakdown.rows.map(function (b) {
            return '<tr><th>' + esc(b.label) + '</th><td>' +
              (b.hasCount ? (Math.round(b.lpm * 100) / 100) + ' L/min' : '&mdash;') + '</td><td>' +
              (b.hasCount
                ? b.count + ' at ' + b.per + (b.perUnit === 'lph' ? ' L/hour' : ' L/min') + ' each'
                : 'no count entered &mdash; this row contributed nothing') +
              '</td></tr>';
          }).join('') + '</tbody></table>'
        : '') +

      '<h4 class="sizer__h">How that total head is made up</h4>' +
      '<table class="sizer__table"><tbody>' +
        (r.depth > 0
          ? '<tr><th>Lift out of the water</th><td>' + r.depth + ' m</td><td>Water surface up to the pump &mdash; this is real head and is often the biggest term</td></tr>'
          : '') +
        '<tr><th>Rise to the emitters</th><td>' + r.rise + ' m</td><td>Pump up to the highest emitter</td></tr>' +
        '<tr><th>Friction loss</th><td>' + r.friction + ' m</td><td>' + r.pipeRun + ' m of ' + r.pipeUsed.od +
          ' mm ' + esc(r.pipeClassLabel.split(' (')[0]) + ' at ' + r.velocity + ' m/s, plus 10% for fittings</td></tr>' +
        '<tr><th>Filter and valves</th><td>' + r.componentM + ' m</td><td>Design allowance across filtration and the zone valve</td></tr>' +
        '<tr><th>Emitter pressure</th><td>' + r.emitterM + ' m</td><td>' + esc(r.emitterLabel) +
          ' need ' + r.emitterBar + ' bar at the nozzle</td></tr>' +
        (r.mainsM > 0
          ? '<tr><th>Less mains pressure</th><td>&minus;' + r.mainsM + ' m</td><td>Your incoming ' + r.mainsBar + ' bar does this much of the work already</td></tr>'
          : '') +
        '<tr class="sizer__total"><th>Duty point</th><td>' + r.totalHead + ' m</td><td>' + r.flow +
          ' L/min at ' + r.totalBar + ' bar</td></tr>' +
      '</tbody></table>' +

      '<h4 class="sizer__h">What that points to</h4>' +
      '<div class="sizer__type"><strong>' + esc(r.type.name) + '</strong><p>' + esc(r.type.why) + '</p></div>' +
      '<p class="sizer__margin">Allowing the 15% margin a pump needs to sit mid-curve rather than flat out at the end of it, ' +
        'look for at least <strong>' + r.requiredFlow + ' L/min</strong> at <strong>' + r.requiredHead + ' m</strong>.</p>' +

      alertBlock(r.warnings) + alertBlock(r.notes, 'note');

      if (m.active && m.fits.length) {
        html += '<h4 class="sizer__h">Pumps that cover this duty point</h4><ul class="sizer__matches">' +
          m.fits.slice(0, 3).map(function (f) {
            return '<li><a href="' + esc(f.p.url) + '"><strong>' + esc(f.p.title) + '</strong></a>' +
              '<span>Delivers ' + f.avail + ' m at your ' + r.flow + ' L/min' +
              (f.curveBased ? ' (from its published curve)' : ' (estimated from its shutoff curve)') +
              (f.p.priceFormatted ? ' &middot; ' + esc(f.p.priceFormatted) : '') + '</span></li>';
          }).join('') + '</ul>';
      } else if (m.active) {
        html += '<h4 class="sizer__h">Nothing in the range covers this</h4>' +
          '<p class="sizer__none">Your duty point is outside what we currently list. That is worth knowing rather than working around &mdash; ' +
          'here is the closest, and exactly why each falls short:</p><ul class="sizer__matches sizer__matches--near">' +
          m.near.map(function (n) {
            return '<li><a href="' + esc(n.p.url) + '"><strong>' + esc(n.p.title) + '</strong></a>' +
              '<span>' + esc(n.why) + '</span></li>';
          }).join('') + '</ul>';
      } else {
        html += '<h4 class="sizer__h">Which pump, specifically?</h4>' +
          '<p class="sizer__none">The figures above are the hard part and they are yours to keep &mdash; take them to any supplier. ' +
          'We match them to a specific pump by hand rather than automatically, so send them over and we will come back with a model ' +
          'and its curve, free and with no obligation.</p>';
      }

      html += '<div class="sizer__cta">' +
        '<a class="btn btn--primary btn--lg" href="' + esc(quoteHref(input, r)) + '">' +
          'Send these figures to us</a>' +
        '<button type="button" class="btn btn--outline btn--lg" data-sizer-print>Print or save as PDF</button>' +
      '</div>' +
      '<p class="sizer__disclaimer">Indicative only, and calculated entirely from the figures you entered. ' +
        'Friction loss assumes PE pipe in good condition. Check it against the pump curve, and against your own site, before you order. ' +
        'Any fixed wiring must be done by a registered electrician.</p>';

      out.hidden = false;
      out.innerHTML = html;
      var pr = $('[data-sizer-print]', out);
      if (pr) pr.addEventListener('click', function () { window.print(); });
      out.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = readInput();
      render(input, S.calculate(input));
    });
    form.addEventListener('reset', function () {
      out.hidden = true; out.innerHTML = '';
      setTimeout(function () { syncDepth(); syncMode(); }, 0);
    });
  }

  /* --- prefill the contact form when arriving from the calculator --- */
  function prefillContact() {
    var form = $('#quote form') || $('form[action*="/contact"]');
    if (!form || !window.location.search) return;
    var q = new URLSearchParams(window.location.search);
    var map = {
      flow: 'contact[Flow needed L-min]', lift: 'contact[Vertical lift m]',
      run: 'contact[Pipe run m]', source: 'contact[Water source]',
      app: 'contact[Watering what]', power: 'contact[Power on site]',
      body: 'contact[body]'
    };
    var filled = false;
    Object.keys(map).forEach(function (key) {
      var val = q.get(key);
      if (val == null || val === '') return;
      var field = form.querySelector('[name="' + map[key] + '"]');
      if (!field) return;
      if (field.tagName === 'SELECT') {
        var hit = Array.prototype.filter.call(field.options, function (o) {
          return o.value === val || o.textContent.trim() === val;
        })[0];
        if (hit) { field.value = hit.value; filled = true; }
      } else { field.value = val; filled = true; }
    });
    if (filled) {
      var reason = form.querySelector('[name="contact[Enquiry type]"]');
      if (reason) {
        var opt = Array.prototype.filter.call(reason.options, function (o) {
          return /quote|sizing/i.test(o.textContent); })[0];
        if (opt) reason.value = opt.value;
      }
      var note = document.createElement('p');
      note.className = 'form-note';
      note.innerHTML = '<strong>Your sizing figures have been carried over.</strong> ' +
        'Check them, add your contact details and send.';
      form.insertBefore(note, form.firstChild);
      // keep the figures out of the address bar once they are in the form
      if (window.history.replaceState) {
        window.history.replaceState({}, '', window.location.pathname + '#quote');
      }
    }
  }

  function boot() {
    $$('[data-sizer]').forEach(init);
    prefillContact();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
